import React from 'react';
import { motion } from 'framer-motion';
import { PencilSimple, Check, Warning, Stethoscope, Star } from '@phosphor-icons/react';

const SEVERITY_BADGE = {
  mild: 'bg-emerald-100 text-emerald-700',
  moderate: 'bg-amber-100 text-amber-700',
  severe: 'bg-red-100 text-red-700',
};

const GOAL_LABELS = {
  track_patterns: 'Track symptoms & patterns',
  doctor_prep: 'Doctor prep',
  understand_conditions: 'Understand conditions',
  healthy_habits: 'Healthier habits',
  manage_medications: 'Manage medications',
  talk_health: 'Health support',
  mental_health: 'Mental health',
  health_record: 'Health record',
};

const FREQ_LABELS = {
  once_daily: 'Once daily', twice_daily: 'Twice daily', three_daily: 'Three times daily',
  morning: 'Morning', night: 'Night', as_needed: 'As needed', weekly: 'Weekly',
};

const SummarySection = ({ title, stepNum, onEdit, empty, emptyLabel, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-medium text-slate-800">{title}</h3>
      <button onClick={() => onEdit(stepNum)}
        className="text-xs text-slate-400 hover:text-[#235347] transition-colors flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-slate-50"
        data-testid={`edit-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <PencilSimple size={14} />
        <span>{empty ? 'Add' : 'Edit'}</span>
      </button>
    </div>
    {empty ? (
      <p className="text-xs text-slate-400 italic">{emptyLabel || 'Skipped — you can add this later'}</p>
    ) : (
      children
    )}
  </div>
);

export const OnboardingSummary = ({ data, onEditStep }) => {
  const {
    conditions = [], suspected_conditions = [], medications = [],
    current_state = {}, medical_history = {}, life_context = {},
    goals = {}, medical_team = [],
  } = data;

  const hasConditions = conditions.length > 0 || suspected_conditions.length > 0;
  const hasMeds = medications.length > 0;
  const hasCurrentState = current_state.feeling_description || (current_state.current_issues || []).length > 0 || current_state.overall_signal;
  const hasHistory = !medical_history.nothing_significant && (
    (medical_history.past_conditions || []).length > 0 ||
    (medical_history.hospitalisations || []).length > 0 ||
    (medical_history.surgeries || []).length > 0 ||
    (medical_history.recurring_issues || []).length > 0
  );
  const hasTeam = medical_team.length > 0;
  const hasLifeContext = life_context.work || life_context.stress_level || life_context.sleep_quality;
  const hasGoals = (goals.selected || []).length > 0;

  const missing = [];
  if (!hasConditions) missing.push('Conditions');
  if (!hasMeds) missing.push('Medications');
  if (!hasCurrentState) missing.push('Current state');
  if (!hasHistory && !medical_history.nothing_significant) missing.push('Medical history');
  if (!hasTeam) missing.push('Care team');
  if (!hasLifeContext) missing.push('Life context');

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      <div className="bg-white border border-slate-150 rounded-2xl p-5">
        <p className="text-slate-700 leading-relaxed">
          Here's what I've captured. Tap <span className="text-[#235347] font-medium">Edit</span> on any section to make changes.
        </p>
        <p className="text-xs text-slate-400 mt-2">
          You can always update everything later from your health profile.
        </p>
      </div>

      {/* Conditions — step 1 */}
      <SummarySection title="Conditions" stepNum={1} onEdit={onEditStep} empty={!hasConditions}>
        <div className="space-y-2">
          {conditions.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <Check size={14} className="text-emerald-600" weight="bold" />
              <span className="text-sm text-slate-700">{c.name}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${SEVERITY_BADGE[c.severity] || 'bg-slate-100 text-slate-600'}`}>
                {c.severity}
              </span>
              {c.status === 'suspected' && (
                <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded-full">suspected</span>
              )}
            </div>
          ))}
          {suspected_conditions.map((sc, i) => (
            <div key={`s-${i}`} className="flex items-center gap-2">
              <Warning size={14} className="text-amber-500" />
              <span className="text-sm text-slate-500 italic">Suspected: {sc.name}</span>
            </div>
          ))}
        </div>
      </SummarySection>

      {/* Medications — step 2 */}
      <SummarySection title="Medications" stepNum={2} onEdit={onEditStep} empty={!hasMeds}>
        <div className="space-y-1.5">
          {medications.map((m, i) => (
            <div key={i} className="text-sm text-slate-700">
              <span className="font-medium">{m.name}</span>
              {m.dosage && <span className="text-slate-400"> · {m.dosage}</span>}
              {m.frequency && (
                <span className="text-slate-400"> · {FREQ_LABELS[m.frequency] || m.frequency.replace(/_/g, ' ')}</span>
              )}
              {m.purpose && <span className="text-slate-400"> ({m.purpose})</span>}
            </div>
          ))}
        </div>
      </SummarySection>

      {/* Current State — step 3 */}
      <SummarySection title="Current State" stepNum={3} onEdit={onEditStep} empty={!hasCurrentState}>
        {current_state.overall_signal && (
          <p className="text-xs font-medium text-slate-600 mb-1.5 capitalize">{current_state.overall_signal.replace(/_/g, ' ')}</p>
        )}
        {current_state.feeling_description && (
          <p className="text-sm text-slate-600 italic mb-2">"{current_state.feeling_description}"</p>
        )}
        {(current_state.current_issues || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {current_state.current_issues.map(issue => (
              <span key={issue} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                {issue.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
        {current_state.hardest_right_now && (
          <p className="text-xs text-slate-500">Hardest: {current_state.hardest_right_now}</p>
        )}
      </SummarySection>

      {/* Medical History — step 4 */}
      <SummarySection title="Medical History" stepNum={4} onEdit={onEditStep}
        empty={!hasHistory && !medical_history.nothing_significant}
        emptyLabel={medical_history.nothing_significant ? 'Nothing significant' : 'Skipped — you can add this later'}>
        <div className="space-y-1">
          {(medical_history.past_conditions || []).map((pc, i) => (
            <p key={i} className="text-sm text-slate-600">Past: {pc.name} {pc.approximate_year && `(${pc.approximate_year})`}</p>
          ))}
          {(medical_history.surgeries || []).map((s, i) => (
            <p key={`s-${i}`} className="text-sm text-slate-600">Surgery: {s.type} {s.approximate_year && `(${s.approximate_year})`}</p>
          ))}
          {(medical_history.hospitalisations || []).map((h, i) => (
            <p key={`h-${i}`} className="text-sm text-slate-600">Hospital: {h.reason} {h.approximate_year && `(${h.approximate_year})`}</p>
          ))}
          {(medical_history.recurring_issues || []).length > 0 && (
            <p className="text-sm text-slate-600">Recurring: {medical_history.recurring_issues.join(', ')}</p>
          )}
        </div>
      </SummarySection>

      {/* Medical Team — step 5 */}
      <SummarySection title="Care Team" stepNum={5} onEdit={onEditStep} empty={!hasTeam}>
        <div className="space-y-2">
          {medical_team.map((m, i) => (
            <div key={i} className="flex items-center gap-2">
              <Stethoscope size={14} className="text-slate-400" />
              <span className="text-sm text-slate-700">{m.name}</span>
              <span className="text-xs text-slate-400">{m.role}</span>
              {m.is_primary && <Star size={10} weight="fill" className="text-amber-500" />}
              {m.linked_conditions?.length > 0 && (
                <span className="text-[9px] text-[#235347]">{m.linked_conditions.join(', ')}</span>
              )}
            </div>
          ))}
        </div>
      </SummarySection>

      {/* Life Context — step 6 */}
      <SummarySection title="Life Context" stepNum={6} onEdit={onEditStep} empty={!hasLifeContext}>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
          {life_context.work && <span>Work: {life_context.work.replace(/_/g, ' ')}</span>}
          {life_context.stress_level && <span>Stress: {life_context.stress_level.replace(/_/g, ' ')}</span>}
          {life_context.sleep_quality && <span>Sleep: {life_context.sleep_quality.replace(/_/g, ' ')}</span>}
          {life_context.support && <span>Support: {life_context.support.replace(/_/g, ' ')}</span>}
        </div>
        {life_context.additional_notes && (
          <p className="text-xs text-slate-400 mt-1">{life_context.additional_notes}</p>
        )}
      </SummarySection>

      {/* Goals — step 7 */}
      <SummarySection title="Goals" stepNum={7} onEdit={onEditStep} empty={!hasGoals}>
        <div className="flex flex-wrap gap-1.5">
          {(goals.selected || []).map(g => (
            <span key={g} className="text-xs bg-[#235347]/10 text-[#235347] px-2.5 py-1 rounded-full font-medium">
              {GOAL_LABELS[g] || g.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
        {goals.custom && (
          <p className="text-xs text-slate-500 mt-2 italic">"{goals.custom}"</p>
        )}
      </SummarySection>

      {/* Missing info */}
      {missing.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Missing</p>
          <p className="text-xs text-slate-400">
            {missing.join(', ')} — you can add {missing.length === 1 ? 'this' : 'these'} anytime from your profile.
          </p>
        </div>
      )}
    </motion.div>
  );
};
